"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileCheck, ArrowRight, Stethoscope, Award, BookOpen } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface ExpertProfile {
  id: string;
  name: string;
  credentials: string;
  role: string;
  specialty: string;
  experience: string;
  bio: string;
  portraitSrc: string;
  portraitSrcset: string;
  portraitSizes: string;
  articlesCount: string;
  articlesUrl: string;
}
const expertsData: ExpertProfile[] = [{
  id: "dr-elena-vance",
  name: "Dr. Elena Vance",
  credentials: "DVM, DACVIM (Internal Medicine)",
  role: "Veterinary Medical Director",
  specialty: "Canine Gastroenterology & Renal Health",
  experience: "14 years clinical practice",
  bio: "Former clinical professor and practicing hospital clinician. She oversees PetsWhirl's diagnostic guides and reviews internal medicine content for accuracy.",
  portraitSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/expert_elena_vance.png",
  portraitSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/expert_elena_vance.png-wvc-srcset",
  portraitSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/expert_elena_vance.png-wvc-sizes",
  articlesCount: "84 reviewed guides",
  articlesUrl: "/PetHealthPage"
}, {
  id: "dr-marcus-thorne",
  name: "Dr. Marcus Thorne",
  credentials: "DVM, PhD in Veterinary Nutrition",
  role: "Senior Nutrition Fellow",
  specialty: "Feline Metabolic Care & Clinical Dietetics",
  experience: "11 years veterinary research",
  bio: "Leads clinical evaluations of ingredient safety, macro-nutrient requirements, and evidence-informed feeding protocols across life stages for cats and dogs.",
  portraitSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/expert_marcus_thorne.png",
  portraitSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/expert_marcus_thorne.png-wvc-srcset",
  portraitSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/expert_marcus_thorne.png-wvc-sizes",
  articlesCount: "62 reviewed guides",
  articlesUrl: "/NutritionPage"
}, {
  id: "sarah-lindqvist",
  name: "Sarah Lindqvist",
  credentials: "MS, CAAB, CPDT-KA",
  role: "Lead Behavior Specialist",
  specialty: "Applied Animal Behavior & Conditioning",
  experience: "9 years animal behaviorist",
  bio: "Specializes in canine reactivity, separation anxiety management, and fear-free training protocols based on modern behavioral science.",
  portraitSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/expert_sarah_lindqvist.png",
  portraitSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/expert_sarah_lindqvist.png-wvc-srcset",
  portraitSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/expert_sarah_lindqvist.png-wvc-sizes",
  articlesCount: "49 reviewed guides",
  articlesUrl: "/BehaviorTrainingPage"
}, {
  id: "dr-julian-reid",
  name: "Dr. Julian Reid",
  credentials: "BVSc, MRCVS, FANZCVS",
  role: "Clinical Review Board Member",
  specialty: "Preventive Care & Geriatric Health",
  experience: "16 years emergency & general care",
  bio: "Focuses on early symptom detection, preventive vaccination timelines, and senior pet mobility protocols across diverse canine and feline breeds.",
  portraitSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/expert_julian_reid.png",
  portraitSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/expert_julian_reid.png-wvc-srcset",
  portraitSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/expert_julian_reid.png-wvc-sizes",
  articlesCount: "71 reviewed guides",
  articlesUrl: "/PetHealthPage"
}];
export default function Experts() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="meet-our-experts" data-nav="light" className="bg-background text-foreground border-b border-border py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            <Award className="size-3.5 text-foreground" />
            <span>Veterinary review board</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.2] mb-4">
            The veterinarians and researchers behind our guides
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every clinical recommendation, nutritional breakdown, and behavior guide is written or evaluated by licensed professionals who verify medical accuracy before publication.
          </p>
        </div>

        {/* Clinical Governance Highlight Bar */}
        <div className="mb-12 rounded-lg bg-card border border-border border-l-4 border-l-foreground p-5 sm:p-6 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start sm:items-center gap-3">
              <div className="p-2 rounded-md bg-muted text-foreground shrink-0 mt-0.5 sm:mt-0">
                <FileCheck className="size-5" />
              </div>
              <div>
                <h3 className="font-sans font-semibold text-sm sm:text-base text-foreground">
                  Our evidence standard
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Content undergoes peer-level review against current veterinary literature and clinical safety guidelines.
                </p>
              </div>
            </div>

            <Button asChild variant="outline" size="sm" className="self-start sm:self-center border-border hover:bg-muted text-xs font-medium">
              <Link to="/AboutPetsWhirlPage">
                Read review protocols
              </Link>
            </Button>
          </div>
        </div>

        {/* Experts Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {expertsData.map((expert, i) => <div key={expert.id} data-index={i}>
              <motion.div variants={itemVariants} className="h-full">
                <Card className="h-full bg-card border-border rounded-xl shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-border hover:shadow-md flex flex-col justify-between overflow-hidden p-0">
                  <CardContent className="p-6 sm:p-8 flex flex-col h-full justify-between gap-6">
                    {/* Top Identity Block */}
                    <div className="flex flex-col sm:flex-row gap-5 items-start">
                      <div className="relative shrink-0 overflow-hidden rounded-xl border border-border bg-muted size-24 sm:size-28">
                        <img src={expert.portraitSrc} data-wvc-srcset={expert.portraitSrcset} data-wvc-sizes={expert.portraitSizes} alt={`${expert.name} portrait`} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                      </div>

                      <div className="space-y-1.5 flex-1 min-w-0">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-muted border border-border text-[11px] font-medium text-muted-foreground">
                          <Stethoscope className="size-3 text-foreground" />
                          <span>{expert.role}</span>
                        </div>

                        <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground tracking-tight pt-1">
                          {expert.name}
                        </h3>

                        <p className="text-xs font-medium text-muted-foreground leading-snug">
                          {expert.credentials}
                        </p>
                      </div>
                    </div>

                    {/* Metadata & Bio */}
                    <div className="space-y-4 pt-2 border-t border-border">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground block">Specialty</span>
                          <span className="font-medium text-foreground">{expert.specialty}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block">Experience</span>
                          <span className="font-medium text-foreground">{expert.experience}</span>
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {expert.bio}
                      </p>
                    </div>

                    {/* Footer Action Link */}
                    <div className="pt-4 border-t border-border flex items-center justify-between mt-auto">
                      <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <BookOpen className="size-3.5 text-muted-foreground" />
                        {expert.articlesCount}
                      </span>

                      <Button asChild variant="ghost" size="sm" className="text-xs font-semibold text-foreground hover:bg-muted p-2 h-auto gap-1">
                        <Link to={expert.articlesUrl}>
                          <span>View reviewed topics</span>
                          <ArrowRight className="size-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>)}
        </motion.div>

        {/* Bottom Disclaimer */}
        <div className="mt-12 text-center max-w-2xl mx-auto">
          <p className="text-xs text-muted-foreground leading-normal">
            PetsWhirl educational material is reviewed for accuracy and does not replace in-person diagnosis, clinical examination, or tailored veterinary emergency treatment.
          </p>
        </div>
      </div>
    </section>;
}